import { Link } from 'react-router-dom';
import SchemaOrg from '../../../components/base/SchemaOrg';
import { useEffect } from 'react';

export default function DrainCleaningPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const faqItems = [
    {
      question: '水溝多久需要清理一次？',
      answer: '建議每年至少清理2-3次，特別是在雨季前後。如果水溝位於樹木較多的地區，或是經常有落葉堆積，建議每季清理一次。定期清理可以預防淤積造成的排水不良。'
    },
    {
      question: '水溝清理包含哪些項目？',
      answer: '包含：清除淤泥、落葉、垃圾等雜物；疏通排水孔；檢查水溝結構；清洗水溝內壁；必要時進行消毒除臭。我們會徹底清理，確保排水順暢。'
    },
    {
      question: '清理水溝會很髒亂嗎？',
      answer: '不會。我們使用專業設備和防護措施，清理過程會妥善處理廢棄物，不會弄髒周圍環境。清理完成後會清潔現場，確保環境整潔。'
    },
    {
      question: '水溝清理需要多久時間？',
      answer: '一般住宅水溝清理約2-3小時，大型社區或商業建築可能需要半天到一天。實際時間依水溝長度、淤積程度而定，我們會在現場評估後告知。'
    },
    {
      question: '水溝清理費用如何計算？',
      answer: '費用依水溝長度、深度、淤積程度而定。一般住宅水溝清理約$20,000起，實際價格需現場評估。我們提供免費到府估價，報價透明合理。'
    },
    {
      question: '清理後的廢棄物如何處理？',
      answer: '我們會將清理出的廢棄物妥善打包，依照環保法規送至合法處理場所。不會隨意傾倒，確保符合環保規定。'
    }
  ];

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${siteUrl}/services/drain-cleaning#service`,
      name: '水溝清理服務',
      description: '專業水溝清理服務，徹底清除淤積物，恢復排水順暢。提供定期維護方案，預防排水問題。',
      provider: {
        '@type': 'LocalBusiness',
        name: '永業環保',
        telephone: '0800-281-281',
        url: siteUrl
      },
      areaServed: [
        { '@type': 'City', name: '桃園市' },
        { '@type': 'City', name: '新北市' },
        { '@type': 'City', name: '台北市' },
        { '@type': 'City', name: '新竹市' },
        { '@type': 'City', name: '苗栗縣' }
      ],
      offers: {
        '@type': 'Offer',
        price: '20000',
        priceCurrency: 'TWD'
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${siteUrl}/services/drain-cleaning#faqpage`,
      mainEntity: faqItems.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${siteUrl}/services/drain-cleaning#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首頁', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: '服務項目', item: `${siteUrl}/services` },
        { '@type': 'ListItem', position: 3, name: '水溝清洗', item: `${siteUrl}/services/drain-cleaning` }
      ]
    }
  ];

  useEffect(() => {
    const pageTitle = '水溝清理服務｜專業排水溝清洗｜永業環保';
    const pageDesc = '永業環保提供專業水溝清理服務，徹底清除淤積物，恢復排水順暢。預防颱風季節淹水，服務桃園、新北、台北、新竹、苗栗地區，定期維護方案，收費透明合理。';
    const pageKeywords = '水溝清理,排水溝清洗,水溝疏通,排水系統,淹水預防,桃園水溝';
    const pageUrl = `${siteUrl}/services/drain-cleaning`;

    document.title = pageTitle;

    const setMeta = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', pageDesc);
    setMeta('meta[name="keywords"]', 'content', pageKeywords);
    setMeta('link[rel="canonical"]', 'href', pageUrl);
    setMeta('meta[property="og:title"]', 'content', pageTitle);
    setMeta('meta[property="og:description"]', 'content', pageDesc);
    setMeta('meta[property="og:url"]', 'content', pageUrl);
    setMeta('meta[name="twitter:title"]', 'content', pageTitle);
    setMeta('meta[name="twitter:description"]', 'content', pageDesc);

    window.scrollTo(0, 0);
  }, [siteUrl]);

  const services = [
    {
      icon: 'ri-home-line',
      title: '住宅水溝',
      description: '陽台、庭院、地下室排水溝清理',
      items: ['陽台排水溝', '庭院水溝', '地下室排水', '圍牆排水孔']
    },
    {
      icon: 'ri-building-line',
      title: '社區大樓',
      description: '公共區域排水系統維護',
      items: ['地下停車場', '公共走道', '中庭花園', '屋頂排水']
    },
    {
      icon: 'ri-road-line',
      title: '道路水溝',
      description: '道路兩側排水溝渠清潔',
      items: ['人行道水溝', '馬路側溝', '集水井', '雨水箱涵']
    },
    {
      icon: 'ri-store-line',
      title: '商業場所',
      description: '店面、工廠排水系統清理',
      items: ['店面排水', '工廠水溝', '倉庫排水', '停車場排水']
    }
  ];

  const process = [
    {
      step: '1',
      title: '現場勘查',
      description: '評估水溝狀況、堵塞程度與清理範圍',
      icon: 'ri-search-line'
    },
    {
      step: '2',
      title: '清除雜物',
      description: '移除水溝內的落葉、垃圾、淤泥等雜物',
      icon: 'ri-delete-bin-line'
    },
    {
      step: '3',
      title: '高壓沖洗',
      description: '使用高壓水柱徹底沖刷水溝壁面與底部',
      icon: 'ri-water-flash-line'
    },
    {
      step: '4',
      title: '疏通管線',
      description: '確保排水管線暢通，水流順暢排出',
      icon: 'ri-arrow-right-line'
    },
    {
      step: '5',
      title: '環境清潔',
      description: '清理施工現場，恢復環境整潔',
      icon: 'ri-brush-line'
    },
    {
      step: '6',
      title: '完工驗收',
      description: '測試排水功能，確認清理效果',
      icon: 'ri-checkbox-circle-line'
    }
  ];

  const problems = [
    {
      icon: 'ri-water-percent-line',
      title: '積水不退',
      description: '大雨過後積水無法排出，造成環境髒亂與蚊蟲孳生',
      solution: '定期清理水溝，保持排水暢通'
    },
    {
      icon: 'ri-bug-line',
      title: '蚊蟲孳生',
      description: '水溝淤積產生臭味，成為蚊蟲繁殖的溫床',
      solution: '清除淤泥與雜物，消除孳生源'
    },
    {
      icon: 'ri-flood-line',
      title: '淹水風險',
      description: '颱風豪雨時排水不及，造成淹水災害',
      solution: '颱風季前預防性清理，降低淹水風險'
    },
    {
      icon: 'ri-plant-line',
      title: '雜草叢生',
      description: '水溝內雜草生長，阻礙水流並加速淤積',
      solution: '清除雜草並定期維護'
    }
  ];

  const maintenance = [
    {
      season: '春季（3-5月）',
      tasks: ['清除冬季落葉累積', '檢查排水孔是否暢通', '修補破損水溝蓋'],
      frequency: '建議清理 1 次'
    },
    {
      season: '夏季（6-8月）',
      tasks: ['颱風季前預防性清理', '加強雨水排水系統檢查', '清除雜草與淤泥'],
      frequency: '建議清理 2 次',
      important: true
    },
    {
      season: '秋季（9-11月）',
      tasks: ['清除落葉與雜物', '檢查集水井功能', '準備冬季排水'],
      frequency: '建議清理 1 次'
    },
    {
      season: '冬季（12-2月）',
      tasks: ['清除年末累積雜物', '檢查水溝結構', '規劃來年維護計畫'],
      frequency: '建議清理 1 次'
    }
  ];

  const tips = [
    {
      icon: 'ri-calendar-line',
      title: '定期清理',
      description: '建議每季清理一次，颱風季前加強清理'
    },
    {
      icon: 'ri-eye-line',
      title: '日常檢查',
      description: '大雨後檢查排水狀況，發現問題及時處理'
    },
    {
      icon: 'ri-forbid-line',
      title: '避免堵塞',
      description: '不要將垃圾、落葉丟入水溝，保持清潔'
    },
    {
      icon: 'ri-tools-line',
      title: '專業維護',
      description: '嚴重堵塞或結構問題，請交由專業團隊處理'
    }
  ];

  const faqs = [
    {
      question: '水溝多久需要清理一次？',
      answer: '建議每季清理一次，特別是在颱風季節前（5-6月）應加強清理。如果您家附近有大樹，落葉較多，建議增加清理頻率至每 2 個月一次。定期清理可以預防淹水，也能避免蚊蟲孳生。'
    },
    {
      question: '水溝清理的費用如何計算？',
      answer: '費用依清理長度、堵塞程度、施工難度而定。一般出車費用為20000 元起，社區大樓或商業場所需現場評估報價。我們提供現場勘查，報價透明公開，不會有額外收費。'
    },
    {
      question: '清理水溝會很吵嗎？',
      answer: '清理過程會使用高壓水柱與清潔工具，會有一定的聲響，但我們會盡量降低噪音。一般住宅水溝清理時間約 1-2 小時，我們會選擇適當時段施工，避免影響您的作息。'
    },
    {
      question: '颱風季前一定要清理水溝嗎？',
      answer: '非常建議！颱風季節雨量大，如果水溝堵塞，很容易造成淹水。颱風季前清理水溝，可以確保排水系統正常運作，大幅降低淹水風險。這是最重要的預防性維護工作。'
    },
    {
      question: '水溝清理後還會再堵塞嗎？',
      answer: '水溝會因為落葉、垃圾、淤泥等逐漸累積而再次堵塞，這是正常現象。定期清理可以保持排水暢通，延長清理週期。我們也提供定期維護合約，幫您安排固定清理時間。'
    },
    {
      question: '可以自己清理水溝嗎？',
      answer: '簡單的表面清理可以自己做，但深度清潔、管線疏通、高處水溝等，建議交由專業團隊處理。我們有專業設備與安全防護，清理效果更徹底，也更安全。'
    }
  ];

  const relatedServices = [
    {
      title: '水管水刀疏通',
      description: '高壓水刀技術，快速疏通各種管線堵塞',
      icon: 'ri-water-flash-line',
      link: '/services/pipe-cleaning'
    },
    {
      title: '內視鏡檢查',
      description: '精密檢測管線內部狀況，找出問題根源',
      icon: 'ri-search-eye-line',
      link: '/services/endoscope'
    },
    {
      title: '水塔清洗',
      description: '定期清洗水塔，確保飲用水安全',
      icon: 'ri-drop-line',
      link: '/services/water-tank'
    }
  ];

  return (
    <>
      <SchemaOrg schema={schemas} />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-emerald-50 via-white to-green-50 pt-32 pb-20 md:pt-32 md:pb-20 py-20">
          <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/38c072c4c0deb3562a14ecc8c1e74609.jpg')] bg-cover bg-center opacity-5"></div>
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <i className="ri-contrast-drop-line"></i>
                <span>專業水溝清洗服務</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 font-serif">
                水溝清洗服務
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
                定期清理排水溝渠，預防淹水與蚊蟲孳生<br className="hidden md:block" />
                專業團隊、完善設備，讓您的環境保持清潔與安全
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4">
                <Link
                  to="/contact"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-emerald-600 text-white px-8 py-4 rounded-lg font-medium hover:bg-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  <i className="ri-calendar-check-line text-lg"></i>
                  <span>預約清理服務</span>
                </Link>
                <a
                  href="tel:0800-281-281"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-emerald-600 px-8 py-4 rounded-lg font-medium hover:bg-gray-50 transition-all duration-300 border-2 border-emerald-600 whitespace-nowrap"
                >
                  <i className="ri-phone-line text-lg"></i>
                  <span>0800-281-281</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  服務範圍
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  各類水溝清理，我們都能提供專業服務
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {services.map((service, index) => (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-emerald-50 to-white p-6 rounded-xl border border-emerald-100 hover:border-emerald-300 hover:shadow-xl transition-all duration-300 group"
                  >
                    <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-green-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <i className={`${service.icon} text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {service.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      {service.description}
                    </p>
                    <ul className="space-y-2">
                      {service.items.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                          <i className="ri-check-line text-emerald-600"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-12 md:py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  清洗流程
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  六大步驟，確保水溝清潔徹底
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {process.map((step, index) => (
                  <div
                    key={index}
                    className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {step.step}
                      </div>
                      <div className="flex-1">
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mb-3 group-hover:bg-emerald-600 transition-colors duration-300">
                          <i className={`${step.icon} text-xl text-emerald-600 group-hover:text-white transition-colors duration-300`}></i>
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          {step.title}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Problems */}
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  常見水溝問題
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  這些問題，定期清理就能預防
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {problems.map((problem, index) => (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl border border-gray-100 hover:border-emerald-200 hover:shadow-lg transition-all duration-300"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${problem.icon} text-2xl text-red-600`}></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {problem.title}
                        </h3>
                        <p className="text-gray-600 mb-4">
                          {problem.description}
                        </p>
                        <div className="flex items-center gap-2 text-emerald-600 font-medium">
                          <i className="ri-lightbulb-line"></i>
                          <span className="text-sm">{problem.solution}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Maintenance */}
        <section className="py-12 md:py-20 bg-gradient-to-br from-emerald-50 via-white to-green-50">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  定期維護建議
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  依季節特性，規劃最佳清理時程
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {maintenance.map((season, index) => (
                  <div
                    key={index}
                    className={`bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 ${
                      season.important ? 'ring-2 ring-emerald-500' : ''
                    }`}
                  >
                    {season.important && (
                      <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium mb-4">
                        <i className="ri-star-line"></i>
                        <span>重點時期</span>
                      </div>
                    )}
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {season.season}
                    </h3>
                    <div className="mb-4">
                      <div className="text-sm text-gray-500 mb-2">維護重點</div>
                      <ul className="space-y-2">
                        {season.tasks.map((task, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-gray-600">
                            <i className="ri-check-line text-emerald-600"></i>
                            <span>{task}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-2 text-emerald-600 font-medium">
                        <i className="ri-calendar-line"></i>
                        <span>{season.frequency}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-12 bg-gradient-to-br from-emerald-100 to-green-100 p-8 rounded-2xl">
                <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                  日常維護小技巧
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {tips.map((tip, index) => (
                    <div key={index} className="text-center">
                      <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className={`${tip.icon} text-2xl text-emerald-600`}></i>
                      </div>
                      <h4 className="font-bold text-gray-900 mb-2">
                        {tip.title}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {tip.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  常見問題
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  關於水溝清洗的疑問，這裡都有解答
                </p>
              </div>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <details
                    key={index}
                    className="bg-gradient-to-br from-gray-50 to-white p-4 md:p-6 rounded-xl border border-gray-100 hover:border-emerald-200 transition-all duration-300 group"
                  >
                    <summary className="flex items-center justify-between cursor-pointer list-none">
                      <h3 className="text-base md:text-lg font-bold text-gray-900 pr-4">
                        {faq.question}
                      </h3>
                      <i className="ri-arrow-down-s-line text-2xl text-emerald-600 group-open:rotate-180 transition-transform duration-300 flex-shrink-0"></i>
                    </summary>
                    <p className="mt-4 text-sm md:text-base text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-12 md:py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  相關服務
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  更多專業服務，滿足您的需求
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedServices.map((service, index) => (
                  <Link
                    key={index}
                    to={service.link}
                    className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 group"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-green-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <i className={`${service.icon} text-3xl text-emerald-600`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {service.description}
                    </p>
                    <div className="flex items-center gap-2 text-emerald-600 font-medium">
                      <span>了解更多</span>
                      <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform duration-300"></i>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-20 bg-gradient-to-br from-emerald-600 to-green-600 relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Abstract%20water%20drainage%20pattern%20with%20flowing%20lines%20and%20clean%20water%20effect%2C%20minimalist%20design%2C%20soft%20gradient%20background%2C%20fresh%20and%20clean%20atmosphere%2C%20modern%20abstract%20style%2C%20high%20quality%20digital%20art%2C%20emerald%20and%20green%20color%20scheme%2C%20water%20flow%20texture&width=1920&height=400&seq=drain-cleaning-cta-bg&orientation=landscape')] bg-cover bg-center opacity-10"></div>
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 font-serif">
                預防淹水，從定期清理水溝開始
              </h2>
              <p className="text-lg md:text-xl mb-8 opacity-90">
                颱風季節即將來臨，立即預約水溝清理服務<br className="hidden md:block" />
                讓您的環境保持清潔，遠離淹水困擾
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4">
                <Link
                  to="/contact"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-emerald-600 px-8 py-4 rounded-lg font-medium hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  <i className="ri-calendar-check-line text-lg"></i>
                  <span>立即預約</span>
                </Link>
                <a
                  href="tel:0800-281-281"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-transparent text-white px-8 py-4 rounded-lg font-medium hover:bg-white/10 transition-all duration-300 border-2 border-white whitespace-nowrap"
                >
                  <i className="ri-phone-line text-lg"></i>
                  <span>電話諮詢</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
